import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, X, Paperclip } from "lucide-react";
import styles from "./FileDropzone.module.css";

interface FileDropzoneProps {
	files: File[];
	onChange: (files: File[]) => void;
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Sadece dosya seçen/tutan saf bileşen; yükleme mantığı üst bileşene ait. */
export function FileDropzone({ files, onChange }: FileDropzoneProps) {
	const { t } = useTranslation();
	const [dragOver, setDragOver] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const addFiles = (list: FileList | null) => {
		if (!list || list.length === 0) return;
		onChange([...files, ...Array.from(list)]);
	};

	const removeAt = (index: number) => {
		onChange(files.filter((_, i) => i !== index));
	};

	return (
		<div className={styles.root}>
			<div
				className={[styles.dropzone, dragOver && styles.dropzoneActive].filter(Boolean).join(" ")}
				onClick={() => inputRef.current?.click()}
				onDragOver={(e) => {
					e.preventDefault();
					setDragOver(true);
				}}
				onDragLeave={() => setDragOver(false)}
				onDrop={(e) => {
					e.preventDefault();
					setDragOver(false);
					addFiles(e.dataTransfer.files);
				}}
			>
				<Upload size={20} />
				<span>{t("dropFilesHint")}</span>
				<input
					ref={inputRef}
					type="file"
					multiple
					className={styles.hiddenInput}
					onChange={(e) => {
						addFiles(e.target.files);
						e.target.value = "";
					}}
				/>
			</div>
			{files.length > 0 && (
				<div className={styles.list}>
					{files.map((file, index) => (
						<div key={`${file.name}-${index}`} className={styles.item}>
							<Paperclip size={14} />
							<span className={styles.itemName}>{file.name}</span>
							<span className={styles.itemSize}>{formatSize(file.size)}</span>
							<button
								type="button"
								className={styles.itemRemove}
								onClick={(e) => {
									e.stopPropagation();
									removeAt(index);
								}}
								aria-label={t("remove")}
							>
								<X size={14} />
							</button>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
